import { Play, Stop, ArrowsClockwise } from "@phosphor-icons/react";
import type { MouseEvent } from "react";
import type { TtsProviderId, TtsSettings } from "../../shared/tts.js";
import {
  ELEVENLABS_SPEED_MAX,
  ELEVENLABS_SPEED_MIN,
  OPENAI_INSTRUCTIONS_MAX,
  OPENAI_SPEED_MAX,
  OPENAI_SPEED_MIN,
  TTS_MODEL_OPTIONS,
  TTS_PROVIDER_OPTIONS,
  getActiveTtsConfig,
  getCurrentTtsVoice,
  getProviderModels,
  getProviderVoices,
  getTtsProvider,
  resolveTtsSettings,
} from "../../shared/tts.js";

interface PreviewRequest {
  providerId: TtsProviderId;
  voiceId: string;
  modelId: string;
  event: MouseEvent;
}

interface TtsSettingsFormProps {
  value: TtsSettings;
  onChange: (next: TtsSettings) => void;
  compact?: boolean;
  previewingKey?: string | null;
  onPreviewVoice?: (request: PreviewRequest) => void;
  onRegenerate?: () => void;
  regenerateDisabled?: boolean;
}

export function makePreviewKey(providerId: TtsProviderId, voiceId: string): string {
  return `${providerId}:${voiceId}`;
}

export function TtsSettingsForm({
  value,
  onChange,
  compact = false,
  previewingKey,
  onPreviewVoice,
  onRegenerate,
  regenerateDisabled = false,
}: TtsSettingsFormProps) {
  const settings = resolveTtsSettings(value);
  const provider = settings.provider;
  const active = getActiveTtsConfig(settings);
  const providerMeta = getTtsProvider(provider);
  const models = getProviderModels(provider);
  const voices = getProviderVoices(provider);
  const voiceColumns = compact ? "grid-cols-2" : "grid-cols-3";
  const voiceHeight = compact ? "max-h-44" : "max-h-72";
  const sectionGap = compact ? "space-y-3" : "space-y-5";
  const currentVoice = getCurrentTtsVoice(settings);
  const supportsInstructions =
    TTS_MODEL_OPTIONS.find((model) => model.providerId === "openai" && model.id === settings.openai.modelId)
      ?.supportsInstructions === true;

  const setProvider = (nextProvider: TtsProviderId) => {
    onChange(resolveTtsSettings({ ...settings, provider: nextProvider }));
  };

  const updateCommon = (patch: Partial<Pick<TtsSettings, "simpleMode" | "followAlong">>) => {
    onChange(resolveTtsSettings({ ...settings, ...patch }));
  };

  const updateElevenlabs = (patch: Partial<TtsSettings["elevenlabs"]>) => {
    onChange(
      resolveTtsSettings({
        ...settings,
        elevenlabs: { ...settings.elevenlabs, ...patch },
      })
    );
  };

  const updateOpenAi = (patch: Partial<TtsSettings["openai"]>) => {
    onChange(
      resolveTtsSettings({
        ...settings,
        openai: { ...settings.openai, ...patch },
      })
    );
  };

  return (
    <div className={sectionGap}>
      <div>
        <div className={`text-text ${compact ? "text-xs font-medium mb-1.5" : "text-xs font-medium mb-2"}`}>Provider</div>
        <div className={`grid ${compact ? "grid-cols-1 gap-1.5" : "grid-cols-2 gap-2"}`}>
          {TTS_PROVIDER_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => setProvider(option.id)}
              className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                provider === option.id
                  ? "border-accent bg-accent/10 shadow-[inset_0_0_0_1px_rgba(196,149,106,0.12)]"
                  : "border-border bg-surface-alt/60 hover:bg-surface-alt"
              }`}
            >
              <div className={`font-medium ${compact ? "text-xs" : "text-sm"} text-text`}>{option.name}</div>
              <div className={`${compact ? "text-[10px]" : "text-xs"} text-text-muted mt-0.5 leading-relaxed`}>
                {option.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className={`text-text ${compact ? "text-xs font-medium mb-1.5" : "text-xs font-medium mb-2"}`}>Model</div>
        <div className={`grid ${compact ? "grid-cols-1 gap-1.5" : "grid-cols-2 gap-2"}`}>
          {models.map((model) => {
            const selected = active.modelId === model.id;
            return (
              <button
                key={model.id}
                onClick={() => {
                  if (provider === "openai") {
                    updateOpenAi({ modelId: model.id as TtsSettings["openai"]["modelId"] });
                  } else {
                    updateElevenlabs({ modelId: model.id as TtsSettings["elevenlabs"]["modelId"] });
                  }
                }}
                className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                  selected
                    ? "border-accent bg-accent text-white"
                    : "border-border bg-surface-alt/60 hover:bg-surface-alt text-text"
                }`}
              >
                <div className={`font-medium ${compact ? "text-xs" : "text-sm"}`}>{model.name}</div>
                <div className={`${compact ? "text-[10px]" : "text-xs"} mt-0.5 leading-relaxed ${selected ? "text-white/75" : "text-text-muted"}`}>
                  {model.description}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className={`flex items-center justify-between ${compact ? "mb-1.5" : "mb-2"}`}>
          <div className={`text-text ${compact ? "text-xs font-medium" : "text-xs font-medium"}`}>Voice</div>
          <div className={`${compact ? "text-[10px]" : "text-xs"} text-text-muted`}>
            {providerMeta.name} · {currentVoice.name}
          </div>
        </div>
        <div className={`grid ${voiceColumns} gap-2 ${voiceHeight} overflow-y-auto pr-1`}>
          {voices.map((voice) => {
            const selected = voice.id === active.voiceId;
            const key = makePreviewKey(provider, voice.id);
            return (
              <button
                key={voice.id}
                onClick={() => {
                  if (provider === "openai") {
                    updateOpenAi({ voiceId: voice.id });
                  } else {
                    updateElevenlabs({ voiceId: voice.id });
                  }
                }}
                className={`rounded-xl border px-3 py-2 text-left transition-colors flex items-start justify-between gap-2 ${
                  selected
                    ? "border-accent bg-accent text-white"
                    : "border-border bg-surface-alt/60 hover:bg-surface-alt text-text"
                }`}
                title={voice.description}
              >
                <div className="min-w-0">
                  <div className={`font-medium truncate ${compact ? "text-xs" : "text-sm"}`}>{voice.name}</div>
                  <div className={`${compact ? "text-[10px]" : "text-xs"} truncate mt-0.5 ${selected ? "text-white/75" : "text-text-muted"}`}>
                    {voice.description}
                  </div>
                </div>
                {onPreviewVoice ? (
                  <span
                    onClick={(event) =>
                      onPreviewVoice({
                        providerId: provider,
                        voiceId: voice.id,
                        modelId: active.modelId,
                        event,
                      })
                    }
                    className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      selected ? "hover:bg-white/20" : "hover:bg-border"
                    }`}
                    title={previewingKey === key ? "Stop preview" : `Preview ${voice.name}`}
                  >
                    {previewingKey === key ? <Stop size={10} weight="fill" /> : <Play size={10} weight="fill" />}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {provider === "elevenlabs" ? (
        <div className={compact ? "space-y-2.5" : "space-y-3"}>
          <div className={`text-text ${compact ? "text-xs font-medium" : "text-xs font-medium"}`}>Provider Settings</div>
          {settings.elevenlabs.modelId === "eleven_v3" ? (
            <ToggleRow
              compact={compact}
              label="AI audio tags"
              description="Run Claude first to add expressive tags for Eleven v3."
              enabled={settings.elevenlabs.enhanceWithAudioTags}
              onToggle={() =>
                updateElevenlabs({
                  enhanceWithAudioTags: !settings.elevenlabs.enhanceWithAudioTags,
                })
              }
            />
          ) : (
            <>
              <SliderSetting
                compact={compact}
                label="Stability"
                value={settings.elevenlabs.stability}
                onChange={(next) => updateElevenlabs({ stability: next })}
              />
              <SliderSetting
                compact={compact}
                label="Similarity"
                value={settings.elevenlabs.similarityBoost}
                onChange={(next) => updateElevenlabs({ similarityBoost: next })}
              />
              <SliderSetting
                compact={compact}
                label="Style"
                value={settings.elevenlabs.style}
                onChange={(next) => updateElevenlabs({ style: next })}
              />
              <SliderSetting
                compact={compact}
                label="Speed"
                value={settings.elevenlabs.speed}
                min={ELEVENLABS_SPEED_MIN}
                max={ELEVENLABS_SPEED_MAX}
                step={0.05}
                onChange={(next) => updateElevenlabs({ speed: next })}
              />
            </>
          )}
        </div>
      ) : (
        <div className={compact ? "space-y-2.5" : "space-y-3"}>
          <div className={`text-text ${compact ? "text-xs font-medium" : "text-xs font-medium"}`}>Provider Settings</div>
          <SliderSetting
            compact={compact}
            label="Speed"
            value={settings.openai.speed}
            min={OPENAI_SPEED_MIN}
            max={OPENAI_SPEED_MAX}
            step={0.05}
            onChange={(next) => updateOpenAi({ speed: next })}
          />
          <div>
            <div className={`flex items-center justify-between ${compact ? "mb-1" : "mb-1.5"}`}>
              <label className={`${compact ? "text-[10px]" : "text-xs"} text-text-muted`}>Instructions</label>
              <span className={`${compact ? "text-[10px]" : "text-xs"} text-text-muted`}>
                {settings.openai.instructions.length}/{OPENAI_INSTRUCTIONS_MAX}
              </span>
            </div>
            <textarea
              value={settings.openai.instructions}
              onChange={(event) => updateOpenAi({ instructions: event.target.value })}
              disabled={!supportsInstructions}
              rows={compact ? 3 : 4}
              placeholder={
                supportsInstructions
                  ? "Optional style instruction, for example: calm, measured, and reflective."
                  : "This OpenAI model does not expose instruction tuning."
              }
              className={`w-full rounded-xl border px-3 py-2 resize-none transition-colors ${
                supportsInstructions
                  ? "border-border bg-surface-alt/60 text-text placeholder:text-text-muted"
                  : "border-border bg-surface-alt/40 text-text-muted cursor-not-allowed"
              } ${compact ? "text-xs" : "text-sm"}`}
            />
          </div>
        </div>
      )}

      <div className={compact ? "space-y-2" : "space-y-3"}>
        <div className={`text-text ${compact ? "text-xs font-medium" : "text-xs font-medium"}`}>Reading Behavior</div>
        <ToggleRow
          compact={compact}
          label="Simple mode"
          description="Highlight only the current sentence instead of the larger paragraph range."
          enabled={settings.simpleMode}
          onToggle={() => updateCommon({ simpleMode: !settings.simpleMode })}
        />
        <ToggleRow
          compact={compact}
          label="Follow along"
          description="Keep sentence highlighting synced during playback."
          enabled={settings.followAlong}
          onToggle={() => updateCommon({ followAlong: !settings.followAlong })}
        />
      </div>

      {onRegenerate ? (
        <button
          onClick={onRegenerate}
          disabled={regenerateDisabled}
          className={`w-full rounded-xl border border-border bg-surface-alt/70 text-text-muted transition-colors flex items-center justify-center gap-1.5 ${
            compact ? "text-xs px-3 py-2" : "text-sm px-3 py-2.5"
          } hover:text-text disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <ArrowsClockwise size={compact ? 12 : 14} />
          Regenerate audio
        </button>
      ) : null}
    </div>
  );
}

function ToggleRow({
  compact,
  label,
  description,
  enabled,
  onToggle,
}: {
  compact: boolean;
  label: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface-alt/45 px-3 py-2.5 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className={`text-text font-medium ${compact ? "text-xs" : "text-sm"}`}>{label}</div>
        <div className={`${compact ? "text-[10px]" : "text-xs"} text-text-muted mt-0.5 leading-relaxed`}>
          {description}
        </div>
      </div>
      <button
        onClick={onToggle}
        className={`shrink-0 rounded-lg px-2.5 py-1.5 transition-colors ${
          enabled ? "bg-accent text-white" : "bg-surface text-text-muted hover:text-text"
        } ${compact ? "text-[11px]" : "text-xs"}`}
      >
        {enabled ? "On" : "Off"}
      </button>
    </div>
  );
}

function SliderSetting({
  compact,
  label,
  value,
  min = 0,
  max = 1,
  step = 0.05,
  onChange,
}: {
  compact: boolean;
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <div className={`flex justify-between mb-1 ${compact ? "text-[10px]" : "text-xs"} text-text-muted`}>
        <span>{label}</span>
        <span>{value.toFixed(2)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(parseFloat(event.target.value))}
        className="w-full h-1 bg-border rounded-full appearance-none cursor-pointer accent-accent"
      />
    </div>
  );
}
